import React, { useEffect, useRef, useState } from 'react';
import { alpha } from '@material-ui/core';

import styled from 'styled-components';

import {
  CircularProgress,
  IconButton,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';

import { TraceBoardItem } from './interfaces/TraceBoardItem';
import { useTraceBoardContext } from './TraceBoardContext';
import { AddCircleOutlineOutlined } from '@material-ui/icons';
import TraceBoardAddItemDialog from './dialogs/TraceBoardAddItemDialog';
import { TraceBoardColumn } from './interfaces/TraceBoardColumn';

const StyledDiv = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  border-radius: 4px;
  background: #fff;
  box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.08);
  margin-bottom: 10px;
  /* transition: 300ms; */
  min-height: min-content;
  border: 0px solid;
  &:last-child {
    margin-bottom: 0;
    &.selected {
      margin-bottom: -3px;
    }
  }
  &.selected {
    margin: -3px -3px 7px -3px;
    border: 3px solid ${props => alpha(props.theme.palette.primary.main, 0.8)};
    &.external-reference {
      border: 3px solid #fec400;
      border-left: solid 4px #fec400;
      margin-left: -4px;
    }
    &.deleted {
      border: 3px solid ${p => p.theme.palette.error.main};
      border-left: solid 4px ${p => p.theme.palette.error.main};
      margin-left: -4px;
    }
  }
  &.collapsed {
    .title-container {
      padding-bottom: 0;
      .title {
        padding-right: 0;
      }
    }
    .content {
      align-items: center;
      flex-direction: row;
      white-space: nowrap;
      overflow: hidden;
      .description-box {
        overflow: hidden;
        .description {
          padding-left: 8px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
    }
  }
  &.external-reference {
    border-left: solid 4px #fec400;
    margin-left: -4px;
  }
  &.deleted {
    border-left: solid 4px ${p => p.theme.palette.error.main};
    margin-left: -4px;
  }
  &:hover .hover-only {
    height: auto;
    opacity: 1;
    max-height: 100px;
    transition: all 400ms;
  }
  .hover-only {
    transition: all 1000ms;
    overflow: hidden;
    opacity: 0;
    max-height: 0;
  }
  .content {
    display: flex;
    flex-direction: column;
    padding: 12px;
    margin: 0;
  }
  .title-container {
    display: flex;
    align-items: center;
    padding-bottom: 6px;
    white-space: nowrap;
  }
  .title-icon {
    padding-right: 6px;
  }
  .title {
    font-weight: bold;
    padding-right: 12px;
  }
  .title-accessory {
    flex: 1;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    overflow: hidden;
  }
  .description-box {
    .description {
      position: relative;
      z-index: 0;
      white-space: pre-wrap;
      line-height: 1.43;
      &:hover::before {
        content: '';
        display: block;
        position: absolute;
        top: -4px;
        left: -4px;
        right: -4px;
        bottom: -4px;
        background: #f7f8fc;
        border: 1px solid #f4f4f4;
        border-radius: 4px;
        z-index: -1;
      }
    }
  }
  .text-field {
    margin: -4px 0 -4px -6px;
    width: calc(100% + 10px);
    .MuiOutlinedInput-root {
      &.Mui-focused fieldset {
        border-color: #f4f4f4;
      }
    }
    .input {
      padding: 4px 6px;
      line-height: 1.43;
    }
  }
  .link-new-button {
    position: absolute;
    right: -17px;
    top: 50%;
    transform: translateY(-19px);
    .MuiIconButton-label {
      background: #ecf5ff;
      border-radius: 24px;
    }
    svg {
      /* width: 32px;
      height: 32px; */
      color: #2b98f0;
    }
  }
  .card-loading {
    position: absolute;
    display: flex;
    justify-content: center;
    align-items: center;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.75);
    border-radius: 4px;
  }
`;

export interface TraceBoardCardProps {
  rowIndex: number;
  columnIndex: number;
  item: TraceBoardItem;
  disableLinNewItem?: boolean;
  rowColumns: TraceBoardColumn<any>[];
}

const TraceBoardCard: React.FC<TraceBoardCardProps> = ({
  rowIndex,
  columnIndex,
  item,
  rowColumns,
  disableLinNewItem
}) => {
  const {
    columns,
    rowCollapse,
    selected,
    loadingItem,
    canEditItem,
    canLinkNewItem,
    setColumns,
    setSelected,
    setLoadingItem,
    registerItemElement,
    unregisterItemElement,
    onAddItem,
    onUpdateItem
  } = useTraceBoardContext();
  const [edit, setEdit] = useState(item.id === 'new');
  const [description, setDescription] = useState(item.description || '');
  const [openNewDialog, setOpenNewDialog] = useState(false);
  const elementRef = useRef<HTMLDivElement>(null!);
  const column = columns[columnIndex];
  const nextColumn = columns[columnIndex + 1];
  useEffect(() => {
    registerItemElement(rowIndex, columnIndex, item, elementRef.current);
    return () => {
      unregisterItemElement(rowIndex, columnIndex, item);
    };
  }, [rowIndex, columnIndex, item, registerItemElement, unregisterItemElement]);
  const isSelected =
    selected?.columnIndex === columnIndex && selected?.itemId === item.id;
  const isCollapsed = !isSelected && rowCollapse[rowIndex];
  const className = ['board-card'];
  if (isCollapsed && !isSelected) className.push('collapsed');
  if (isSelected) className.push('selected');
  if (item.externalReference) className.push('external-reference');
  // TODO: refactor to use item.deleted
  if (item.data?.deleted) className.push('deleted');
  const onSave = () => {
    setLoadingItem(item);
    if (item.id === 'new') {
      onAddItem?.({ description }, undefined, column, columnIndex);
    } else {
      item.description = description;
      onUpdateItem?.(item, column, columnIndex);
    }
  };
  const {
    renderCard,
    renderTitleIcon,
    renderTitleAccessory,
    renderSubContent
  } = column;
  const renderOptions = {
    rowIndex,
    columnIndex,
    item,
    column,
    isSelected,
    isCollapsed,
    rowColumns,
    elementRef
  };
  if (renderCard) return renderCard(renderOptions);
  const titleIcon = renderTitleIcon
    ? renderTitleIcon(renderOptions)
    : undefined;
  // const isEditable = canEditItem(item, column, columnIndex);
  return (
    <StyledDiv
      ref={elementRef}
      className={className.join(' ')}
      onClick={() => {
        // don't unselect if creating a new linked item
        if (!openNewDialog) {
          setSelected(
            selected?.columnIndex === columnIndex &&
              selected?.itemId === item.id
              ? undefined
              : { columnIndex, itemId: item.id }
          );
        }
      }}
    >
      <div className='content'>
        <div className='title-container'>
          {titleIcon && <div className='title-icon'>{titleIcon}</div>}
          <Typography variant='body1' className='title'>
            {item.title}
          </Typography>
          {renderTitleAccessory && (
            <div className='title-accessory'>
              {renderTitleAccessory(renderOptions)}
            </div>
          )}
        </div>
        {edit ? (
          <TextField
            fullWidth
            autoFocus
            multiline
            variant='outlined'
            size='small'
            value={description}
            placeholder='Description'
            className='text-field'
            InputProps={{
              className: 'input'
            }}
            onClick={event => {
              event.stopPropagation();
              setSelected({ columnIndex, itemId: item.id });
            }}
            onChange={event => setDescription(event.target.value)}
            onKeyDown={event => {
              switch (event.key) {
                case 'Escape':
                  setEdit(false);
                  setDescription(item.description || '');
                  break;
                case 'Enter':
                  setEdit(false);
                  event.preventDefault();
                  if (
                    description.length > 0 &&
                    item.description !== description
                  ) {
                    onSave();
                  }
                  break;
              }
            }}
            onBlur={() => {
              if (item.id === 'new' && description.length === 0) {
                const index = column.items.findIndex(i => i.id === 'new');
                if (index > -1) {
                  column.items.splice(index, 1);
                  setColumns([...columns]);
                }
              } else if (item.description !== description) {
                onSave();
              }
              setEdit(false);
            }}
          />
        ) : (
          <div
            // className={`description-box${isEditable ? ' editable' : ''}`}
            className='description-box'
            onClick={event => {
              if (canEditItem(item, column, columnIndex)) {
                event.stopPropagation();
                setSelected({ columnIndex, itemId: item.id });
                setEdit(true);
              }
            }}
          >
            <Typography
              variant='body2'
              className='description'
              color={item.description ? 'initial' : 'textSecondary'}
            >
              {description || 'Click to add text'}
            </Typography>
          </div>
        )}
      </div>
      {item.id !== 'new' && renderSubContent?.(renderOptions)}
      {isSelected &&
        nextColumn &&
        item.id !== 'new' &&
        !disableLinNewItem &&
        canLinkNewItem(item, column, columnIndex) && (
          <Tooltip
            title={`Link New ${nextColumn.title}`}
            placement='right'
            arrow
          >
            <IconButton
              className='link-new-button'
              size='small'
              onClick={event => {
                event.stopPropagation();
                setOpenNewDialog(true);
              }}
            >
              <AddCircleOutlineOutlined />
            </IconButton>
          </Tooltip>
        )}
      {loadingItem === item && (
        <div className='card-loading'>
          <CircularProgress size={24} color='primary' />
        </div>
      )}
      {openNewDialog && (
        <TraceBoardAddItemDialog
          open
          column={columns[columnIndex + 1]}
          columnIndex={columnIndex + 1}
          sourceItem={item}
          onClose={() => setOpenNewDialog(false)}
        />
      )}
    </StyledDiv>
  );
};
export default TraceBoardCard;
